import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Users, Plus, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["general", "wellness", "creative", "books", "fitness", "tech", "music"];
const CATEGORY_COLORS = { general: "bg-card", wellness: "bg-sage", creative: "bg-accent", books: "bg-secondary", fitness: "bg-sage", tech: "bg-secondary", music: "bg-accent" };

export default function CommunitiesPage() {
  const { user } = useAuth();
  if (!user?.id) return null;
  return <AccountCommunities key={user.id} />;
}

function AccountCommunities() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "general" });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const actionPending = useRef(false);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.get("/communities", { timeout: 30000 });
      if (!mounted.current || version !== loadVersion.current) return;
      if (!Array.isArray(response.data)) throw new Error("Invalid community list");
      setItems(response.data);
    } catch {
      if (mounted.current && version === loadVersion.current) setLoadError(true);
    } finally {
      if (mounted.current && version === loadVersion.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  const closeCreate = () => {
    if (!actionPending.current) setShowCreate(false);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!mounted.current || actionPending.current) return;
    if (!form.name.trim()) {
      toast.error("Community name is required");
      return;
    }
    actionPending.current = true;
    setBusy(true);
    loadVersion.current += 1;
    try {
      const response = await api.post("/communities", { ...form, name: form.name.trim() }, { timeout: 30000 });
      if (!mounted.current) return;
      if (typeof response.data?.id !== "string" || !response.data.id) throw new Error("Missing creation confirmation");
      toast.success("Community created");
      setShowCreate(false); setForm({ name: "", description: "", category: "general" });
    } catch {
      if (mounted.current) toast.error("Could not confirm creation. Check the list before retrying.");
    } finally {
      if (mounted.current) await load();
      actionPending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const join = async (community) => {
    if (!mounted.current || actionPending.current || community.is_owner) return;
    actionPending.current = true;
    setBusy(true);
    loadVersion.current += 1;
    try {
      const r = await api.post(`/communities/${community.id}/join`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      if (typeof r.data?.joined !== "boolean") throw new Error("Missing membership confirmation");
      toast.success(r.data.joined ? "Joined!" : "Left");
    } catch {
      if (mounted.current) toast.error("Could not confirm membership. Check the refreshed list before retrying.");
    } finally {
      // A timed-out toggle may have committed. Reconcile without replaying it.
      if (mounted.current) await load();
      actionPending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const filtered = filter === "all" ? items : items.filter((c) => c.category === filter);

  return (
    <div data-testid="communities-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-4xl">Communities</h1>
          <p className="text-muted-foreground mt-1">Find your people. Smaller circles, warmer talks.</p>
        </div>
        <button data-testid="open-create-community-btn" disabled={busy} onClick={() => { if (!actionPending.current) setShowCreate(true); }} className="brutal-btn bg-primary text-primary-foreground px-4 py-2 flex items-center gap-2">
          <Plus size={16} strokeWidth={2.5} /> New community
        </button>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
            className={`brutal-btn px-3 py-1 text-sm ${filter === c ? "bg-foreground text-background" : "bg-card"}`}>
            {c}
          </button>
        ))}
      </div>

      {loading ? <p role="status">Loading communities…</p> : loadError ? (
        <div role="alert" className="brutal-card p-5 bg-card">
          Could not load communities.
          <button disabled={busy} onClick={load} className="brutal-btn px-3 py-1 ml-3" data-testid="retry-communities">Retry</button>
        </div>
      ) : <div className="grid sm:grid-cols-2 gap-5">
        {filtered.map((c) => (
          <div key={c.id} className={`brutal-card p-5 ${CATEGORY_COLORS[c.category] || "bg-card"}`} data-testid={`community-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <Link to={`/communities/${c.id}`} className="block">
                <h3 className="text-2xl mb-1">{c.name}</h3>
                <p className="text-sm">{c.description}</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <Users size={14} strokeWidth={2.5} /> {c.members_count} members · {c.category}
                </div>
              </Link>
              <button data-testid={`join-${c.id}`} disabled={busy || c.is_owner} onClick={() => join(c)}
                className={`brutal-btn px-3 py-1 text-sm flex-shrink-0 ${c.joined ? "bg-foreground text-background" : "bg-primary text-primary-foreground"}`}>
                {c.is_owner ? "Owner" : c.joined ? "Joined" : "Join"}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="brutal-card p-8 bg-card col-span-full text-center">
            <p>{filter === "all" ? "No communities yet. Be the first to start one." : "No communities in this category yet."}</p>
          </div>
        )}
      </div>}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="create-community-title">
          <button type="button" disabled={busy} aria-label="Close create community dialog" className="absolute inset-0 bg-foreground/60" onClick={closeCreate} />
          <form onSubmit={create} className="relative z-10 brutal-card bg-card p-6 max-w-md w-full" data-testid="create-community-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 id="create-community-title" className="text-2xl">Start a community</h3>
              <button type="button" disabled={busy} aria-label="Close create community dialog" onClick={closeCreate} className="brutal-btn bg-card p-1"><X size={16} strokeWidth={2.5} /></button>
            </div>
            <fieldset disabled={busy} className="space-y-3">
              <input aria-label="Community name" required maxLength={80} placeholder="Community name" className="brutal-input w-full px-3 py-2" data-testid="community-name-input"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <textarea aria-label="Community description" maxLength={1000} placeholder="What's this community about?" rows={3} className="brutal-input w-full px-3 py-2" data-testid="community-desc-input"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <select aria-label="Community category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="brutal-input w-full px-3 py-2" data-testid="community-cat-select">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <button type="submit" className="brutal-btn bg-primary text-primary-foreground w-full py-2" data-testid="create-community-submit">{busy ? "Creating…" : "Create"}</button>
            </fieldset>
          </form>
        </div>
      )}
    </div>
  );
}
